/**
 * SKU定价相关的计算工具函数
 */

import type { ChannelResult } from '../constants/pricingConstants';

/**
 * 自动判断是否旺季 (10月15日 - 1月14日)
 */
export const checkPeakSeason = (): boolean => {
    const now = new Date();
    const month = now.getMonth() + 1; // 1-12
    const day = now.getDate();

    // 10月15日 - 12月31日，或 1月1日 - 1月14日
    return (month === 10 && day >= 15) || (month === 11) || (month === 12) || (month === 1 && day <= 14);
};

/**
 * 计算体积重
 */
export const calculateVolumeWeight = (length: number, width: number, height: number): number => {
    return (length * width * height) / 6000;
};

/**
 * 计算计费重量（取实重和体积重较大值）
 */
export const calculateChargeableWeight = (weight: number, volumeWeight: number): number => {
    return Math.max(weight, volumeWeight);
};

/**
 * 计算完税成本
 */
export const calculateLandedCost = (
    purchaseCostCNY: number,
    shippingCostCNY: number,
    exchangeRate: number,
    dutyRate: number
): number => {
    const purchaseCostUSD = exchangeRate > 0 ? purchaseCostCNY / exchangeRate : 0;
    const shippingCostUSD = exchangeRate > 0 ? shippingCostCNY / exchangeRate : 0;
    const dutyUSD = purchaseCostUSD * (dutyRate / 100);
    return purchaseCostUSD + shippingCostUSD + dutyUSD;
};

/**
 * 计算渠道运费（人民币）
 */
export const calculateChannelShippingCost = (
    rateTiers: any[],
    chargeableWeight: number
): { costCNY: number; matchedRate: number } => {
    if (!rateTiers || rateTiers.length === 0) {
        return { costCNY: 0, matchedRate: 0 };
    }

    // 根据计费重量匹配对应的价格梯度
    let matchedTier = rateTiers[0]; // 默认第一个

    for (const tier of rateTiers) {
        if (chargeableWeight >= (tier.min_weight || 0)) {
            matchedTier = tier;
        } else {
            break; // 已经超出范围，停止搜索
        }
    }

    const pricePerKg = matchedTier.price_per_kg || 0;
    const costCNY = pricePerKg * chargeableWeight;

    return { costCNY, matchedRate: pricePerKg };
};

/**
 * 根据利润率反推售价
 */
export const calculatePriceFromMargin = (
    totalFixedCost: number,
    profitMargin: number,
    referralRate: number,
    returnRate: number,
    otherCostRate: number
): number | null => {
    const marginDecimal = profitMargin / 100;
    const referralRateDecimal = referralRate / 100;
    const returnRateDecimal = returnRate / 100;
    const otherCostRateDecimal = otherCostRate / 100;

    // 分母 = 1 - 佣金率 - 退货率 - 其他成本率 - 目标利润率
    const denominator = 1 - referralRateDecimal - returnRateDecimal - otherCostRateDecimal - marginDecimal;

    if (denominator > 0) {
        return totalFixedCost / denominator;
    }
    return null;
};

/**
 * 根据售价计算利润率
 */
export const calculateMarginFromPrice = (
    price: number,
    totalFixedCost: number,
    referralRate: number,
    returnRate: number,
    otherCostRate: number
): number => {
    if (price <= 0) return 0;

    const referralRateDecimal = referralRate / 100;
    const returnRateDecimal = returnRate / 100;
    const otherCostRateDecimal = otherCostRate / 100;

    // 净收入 = 售价 * (1 - 佣金率 - 退货率 - 其他成本率)
    const netRevenue = price * (1 - referralRateDecimal - returnRateDecimal - otherCostRateDecimal);
    const profit = netRevenue - totalFixedCost;
    const calculatedMargin = (profit / price) * 100;

    return calculatedMargin;
};

/**
 * 计算所有渠道的结果
 */
export const calculateChannelResults = (
    channels: any[],
    chargeableWeight: number,
    exchangeRate: number,
    purchaseCostCNY: number,
    dutyRate: number,
    price: number,
    fbaFee: number,
    referralRate: number,
    currentStorageFee: number,
    returnRate: number,
    otherCostRate: number
): ChannelResult[] => {
    if (channels.length === 0) return [];

    return channels.map((ch, idx) => {
        const { costCNY: sCostCNY } = calculateChannelShippingCost(ch.rate_tiers || [], chargeableWeight);
        const sCostUSD = exchangeRate > 0 ? sCostCNY / exchangeRate : 0;

        const costUSD = exchangeRate > 0 ? purchaseCostCNY / exchangeRate : 0;
        const dutyUSD = costUSD * (dutyRate / 100);
        const lCost = costUSD + sCostUSD + dutyUSD;

        const currentPrice = price || 0;
        const amazonFees = fbaFee + (currentPrice * (referralRate / 100)) + currentStorageFee;
        const variableCosts = currentPrice * ((returnRate + otherCostRate) / 100);
        const totalCost = lCost + amazonFees + variableCosts;
        const profit = currentPrice - totalCost;
        const margin = currentPrice > 0 ? (profit / currentPrice) * 100 : 0;
        const roi = totalCost > 0 ? (profit / totalCost) * 100 : 0;

        return {
            key: (ch.id || idx).toString(),
            channel: `${ch.name} (${ch.transit_days || '?'}天)`,
            shippingCNY: sCostCNY,
            landed: lCost,
            profit,
            margin,
            roi
        };
    });
};

/**
 * 查找最佳渠道（利润率最高）
 */
export const findBestChannel = (results: ChannelResult[]): ChannelResult | null => {
    if (results.length === 0) return null;
    return results.reduce((best, curr) => curr.margin > best.margin ? curr : best, results[0]);
};
